import { CSSInterpolation } from '@emotion/serialize';
import React from 'react';
import { SpaceScale } from '../../theme/tokens';
import { useTheme } from '../../theme';
import { CSSRulesFunction, Theme } from '../../theme/types';
import Icon, { IconNames, IconVariant } from '../Icon';
import VisuallyHidden from '../VisuallyHidden';
import { omit } from '../../utils';
import { ButtonColorVariant, ButtonSize } from './types';
import {
  colorVariantStyling,
  commonButtonStyle,
  unpackButtonColorVariantStyles,
} from './appearance';

interface AppearanceProps {
  colorVariant?: ButtonColorVariant;
  size?: ButtonSize;
}

interface Props {
  icon?: IconNames;
  buttonType?: string;
  disabled?: boolean;
  children: React.ReactNode;
}

type HTMLProps = Omit<
  React.HTMLAttributes<HTMLButtonElement>,
  keyof AppearanceProps | 'children'
>;

export interface IconButtonProps extends Props, AppearanceProps, HTMLProps {}

type SizeStyling = Record<ButtonSize, { padding: SpaceScale }>;

const sizeStyling: SizeStyling = {
  Large: {
    padding: 3,
  },
  Medium: {
    padding: 2,
  },
};

const unpackSizeStyles = (size: ButtonSize, theme: Theme): CSSInterpolation => {
  const { padding } = sizeStyling[size];
  return theme.responsiveSpacing(padding, ['padding']);
};

const iconButtonStyle: CSSRulesFunction<IconButtonProps> = (theme, props) => {
  const variant = props.colorVariant || ButtonColorVariant.Primary;
  const variantColors = colorVariantStyling[variant];
  const commonStyles = commonButtonStyle(theme, props);
  const padding = unpackSizeStyles(props.size || ButtonSize.Medium, theme);

  const processedButtonStyle = {
    ...unpackButtonColorVariantStyles(variantColors.base, theme),
    '&:hover': {
      ...unpackButtonColorVariantStyles(variantColors.hover, theme),
    },
    '&:focus': {
      ...unpackButtonColorVariantStyles(variantColors.focus, theme),
    },
    '&:active': {
      ...unpackButtonColorVariantStyles(variantColors.active, theme),
    },
    '&[aria-disabled=true]': {
      cursor: 'not-allowed',
      ...unpackButtonColorVariantStyles(variantColors.disabled, theme),
    },
  };

  return [commonStyles, padding, processedButtonStyle];
};

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(props, ref) {
    const { theme } = useTheme();
    const { icon = 'clear', id, children, disabled } = props;

    const rest = omit(props, [
      'colorVariant',
      'buttonType',
      'icon',
      'colorVariant',
      'size',
    ]);

    return (
      <button
        aria-labelledby={id}
        ref={ref}
        css={iconButtonStyle(theme, props)}
        aria-disabled={disabled}
        disabled={disabled}
        {...rest}
      >
        <Icon variant={IconVariant.Inherit} name={icon} />
        <VisuallyHidden>{children}</VisuallyHidden>
      </button>
    );
  }
);
export default IconButton;
